export interface Project {
  name: string
  tagline: string
  description: string
  tech: string[]
  status: string
  screenshots: string[]
  focus: string[]
  url?: string
  embedUrl?: string
  metrics?: string[]
  lesson?: string
}

export interface Skill {
  name: string
  level: number
  label: string
}

export interface ExpertiseCategory {
  category: string
  items: string[]
}

export interface CaseStudyBeat {
  label: string
  detail: string
}

export interface CaseStudy {
  title: string
  thesis: string
  beats: CaseStudyBeat[]
  metrics: string[]
}

export interface Experience {
  period: string
  role: string
  company: string
  highlights: string[]
  caseStudy?: CaseStudy
}

export const profile = {
  name: 'Anderson Reinkordt',
  title: 'AI Engineer',
  tagline: 'Accessible Intelligence',
  bio: 'Software engineer building AI-powered tools and accessible applications.',
  company: 'can.code Labs',
  philosophy: '"To teach is to learn"',
  portrait: '/starfighter.webp',
  logo: '/portfolio-logo.webp',
  mission: [
    'Human-centered design',
    'Accessibility-first development',
    'Transparency in what works and what doesn\'t',
    'Learning in the open',
  ],
}

export const projects: Project[] = [
  {
    name: 'TubeNest',
    tagline: 'Safe YouTube Viewing for Children, Controlled by Parents',
    description:
      'Parents connect their YouTube account, curate playlists, and present them on a distraction-free page for their kids. Videos play in YouTube\'s embedded player with no recommendations, sidebar, or related-video rabbit holes.',
    tech: ['Next.js 16', 'TypeScript', 'Tailwind v4', 'Auth.js v5', 'Drizzle ORM', 'Neon PostgreSQL'],
    status: 'Active Development',
    screenshots: [
      '/screenshots/tubenest-1.webp',
      '/screenshots/tubenest-2.webp',
      '/screenshots/tubenest-3.webp',
      '/screenshots/tubenest-4.webp',
    ],
    focus: ['Child Safety', 'Distraction-Free UX', 'Parental Controls'],
    url: 'https://tubenest.online',
    embedUrl: 'https://tubenest.online/embed/XEFFTPe0e9e9aE3ylaxwFmRI',
    metrics: [
      'Google OAuth with YouTube read-only scope',
      'Per-child profiles, PIN protection, and daily time limits',
      'PWA-installable watch pages and embeddable playlists',
      'Household collaboration for co-parents',
    ],
  },
  {
    name: 'YAPS',
    tagline: 'Voice-to-Content Creation Tool',
    description:
      'Voice-first content creation. Record, transcribe with Whisper, and generate polished blog posts — all from your voice.',
    tech: ['HTML/CSS/JS', 'Whisper', 'Claude AI', 'Express'],
    status: 'Active Development',
    screenshots: ['/screenshots/yaps.webp', '/screenshots/yaps2.webp'],
    focus: ['Voice Interfaces', 'Content Creation'],
    metrics: [
      '60+ blog posts generated',
      '15+ tone styles, 8+ output formats',
      'Dual transcription (local Whisper + AssemblyAI)',
      'WCAG 2.1 AA, PWA-installable',
    ],
    lesson:
      'I built voice navigation, then cancelled it. It required internet and pulled attention from the core mission. The project is "create by voice," not "navigate by voice." That distinction matters.',
  },
  {
    name: 'jott',
    tagline: 'AI-Powered Interactive Task Management CLI',
    description:
      'Keyboard-driven task management with AI integration, Google Calendar sync, cross-project routing, and keyword automation. Published on PyPI as jott-cli.',
    tech: ['Python', 'Claude AI', 'Google Calendar', 'MCP'],
    status: 'Published (PyPI)',
    screenshots: ['/screenshots/jott2.webp', '/screenshots/jott.webp'],
    focus: ['Developer Tools', 'AI Integration'],
    metrics: [
      '503 tests, ~7,900 lines of test code',
      '72 modules, ~18,200 lines',
      'Used daily across 80+ projects',
      'Published on PyPI',
    ],
    lesson:
      'Mixin composition over inheritance: TaskManager is 8 mixins, CommandHandler is 11, each under 300 lines. JSON storage over a database means jott works across the entire portfolio without central infrastructure.',
  },
  {
    name: 'jobsearch-cli',
    tagline: 'Intelligent Job Search Automation',
    description:
      'Comprehensive CLI for job search automation: multi-platform job board aggregation, AI-powered job matching (22-skill weighted system), automated cover letter generation, and browser extension for one-click applications.',
    tech: ['Go', 'SQLite', 'Claude AI', 'Cobra', 'Browser Extension'],
    status: 'Production',
    screenshots: ['/screenshots/jobsearch-cli.webp'],
    focus: ['Systems Programming', 'AI Integration'],
    metrics: [
      '23,600+ lines of Go across 98 packages',
      '22 CLI commands, 17 API endpoints',
      'Search across all sources: 2-5s',
      '<100ms startup, <10ms DB queries',
    ],
    lesson:
      'A late refactoring pass extracted 402 lines of inline logic from six resume commands. Average command definition went from 78 lines to 13. Guard clauses replaced the nested conditionals.',
  },
  {
    name: 'tts-extension',
    tagline: 'Text-to-Speech Browser Extension',
    description:
      'Chrome extension with multi-backend TTS: system voices, OpenAI TTS, and Chatterbox AI voice cloning. Voice profile management and accessibility-first design.',
    tech: ['JavaScript', 'Chrome Extension MV3', 'OpenAI TTS', 'Chatterbox AI'],
    status: 'v2.0',
    screenshots: ['/screenshots/tts-extension.webp'],
    focus: ['Accessibility', 'AI Voice Cloning'],
  },
  {
    name: 'interviewer',
    tagline: 'Real-Time AI Interview Assistant',
    description:
      'Live audio capture with real-time Whisper transcription and AI coaching via Claude. Dual mode: Interviewer (contextual question suggestions) and Interviewee (STAR method coaching).',
    tech: ['Go', 'PortAudio', 'Whisper', 'Claude AI', 'Bubble Tea TUI'],
    status: 'v1.0',
    screenshots: ['/screenshots/interviewer.webp'],
    focus: ['Voice AI', 'Real-Time Processing'],
  },
  {
    name: 'Fact Checker',
    tagline: 'AI-Powered Fact-Checking Chrome Extension',
    description:
      'Select text on any webpage, click Fact Check, and get a structured verdict from Claude AI. Claim-by-claim analysis with confidence levels and a six-level verdict scale from Verified to Unverifiable.',
    tech: ['JavaScript', 'Chrome Extension MV3', 'Claude AI'],
    status: 'v1.0',
    screenshots: [
      '/screenshots/factcheck-3.webp',
      '/screenshots/factcheck-4.webp',
      '/screenshots/factcheck-2.webp',
      '/screenshots/factcheck-1.webp',
    ],
    focus: ['AI Integration', 'Browser Extensions'],
  },
  {
    name: 'FirstResponder',
    tagline: 'Multi-Domain AI Voice Assistant Platform',
    description:
      'Native Android app that captures live audio, transcribes speech in real-time, and provides AI-powered guidance through switchable domain profiles. Voice-create custom agents for any professional domain on the fly.',
    tech: ['Kotlin', 'Jetpack Compose', 'Whisper', 'Claude AI'],
    status: 'Active Development',
    screenshots: [
      '/screenshots/firstresponder-3.webp',
      '/screenshots/firstresponder-4.webp',
      '/screenshots/firstresponder-1.webp',
      '/screenshots/firstresponder-2.webp',
    ],
    focus: ['Voice AI', 'Mobile', 'Multi-Domain'],
    metrics: [
      'Three platforms: Android, Go API, Next.js',
      '50ms audio chunks, sentence-boundary buffering',
      '20-message context window with marked preservation',
      'AssemblyAI primary, on-device fallback',
    ],
    lesson:
      'Profile-based domain switching over separate codebases. Infrastructure is shared, behavior is configuration. Adding a new domain is configuration, not development; the AI Domain Creator makes that accessible to non-developers too.',
  },
  {
    name: 'StoryTimeStar',
    tagline: 'AI-Powered Interactive Storytelling for Children',
    description:
      'Voice-enabled storytelling app that creates interactive, age-appropriate stories for children ages 3-13+. Five specialized storyteller agents, real-time AI illustration generation, and voice input for story participation.',
    tech: ['Next.js', 'TypeScript', 'Claude AI', 'AssemblyAI', 'MongoDB'],
    status: 'Production',
    screenshots: [
      '/screenshots/storytimestar-1.webp',
      '/screenshots/storytimestar-2.webp',
      '/screenshots/storytimestar-3.webp',
    ],
    focus: ['Voice AI', 'Education', 'AI Illustration'],
  },
  {
    name: 'therapissed',
    tagline: 'AI-Powered Political Satire Analysis',
    description:
      'Fetches real political quotes and delivers AI-powered psychological analysis through a clinical psychologist persona. Educational satire meets personality disorder awareness.',
    tech: ['Next.js', 'TypeScript', 'TailwindCSS', 'Claude AI', 'Neon Postgres'],
    status: 'Production',
    screenshots: [
      '/screenshots/therapissed1.webp',
      '/screenshots/therapissed2.webp',
      '/screenshots/therapissed3.webp',
    ],
    focus: ['AI Personas', 'Educational Satire'],
  },
  {
    name: 'Ideanas',
    tagline: 'AI-Powered Voice Ideation Platform',
    description:
      'Record voice memos or type ideas, then receive deep introspective analysis from Claude. Multi-turn conversations with themed analysis, insights, and synthesized reflection across web, Android, and iOS.',
    tech: ['React Native', 'Expo', 'TypeScript', 'Claude AI', 'AssemblyAI', 'MongoDB'],
    status: 'v2.1',
    screenshots: ['/screenshots/ideanas-1.webp', '/screenshots/ideanas-2.webp', '/screenshots/ideanas-3.webp'],
    focus: ['Voice AI', 'Cross-Platform', 'AI Analysis'],
  },
  {
    name: 'Rubber Ducky Live',
    tagline: 'Voice-AI Problem-Solving Research Platform',
    description:
      'Voice-enabled chat interface with AI rubber duck companions for thinking out loud and problem-solving. 13+ agent personalities, real-time streaming, session recording, and comprehensive export to PDF, Word, and Google Drive.',
    tech: ['Next.js', 'TypeScript', 'Claude AI', 'AssemblyAI', 'MongoDB', 'Socket.IO'],
    status: 'v0.1.1',
    screenshots: ['/screenshots/rubber-ducky-live-1.webp', '/screenshots/rubber-ducky-live-2.webp', '/screenshots/rubber-ducky-live-3.webp'],
    focus: ['Voice AI', 'Real-Time Collaboration', 'Accessibility'],
  },
]

export const skills: Skill[] = [
  { name: 'Ruby on Rails', level: 100, label: '10+ years' },
  { name: 'TypeScript', level: 80, label: 'Expert' },
  { name: 'JavaScript', level: 80, label: 'Expert' },
  { name: 'Go', level: 65, label: 'Production' },
  { name: 'Python', level: 55, label: 'Advanced' },
  { name: 'SQL', level: 70, label: 'Expert' },
]

export const expertise: ExpertiseCategory[] = [
  {
    category: 'AI Development',
    items: ['Large Language Models', 'Multi-agent AI Systems', 'Prompt Engineering', 'AI-Powered Automation'],
  },
  {
    category: 'Accessibility',
    items: ['WCAG 2.1 AA/AAA', 'Assistive Technology', 'Universal Design', 'Screen Reader Optimization'],
  },
  {
    category: 'Voice Interfaces',
    items: ['Voice-AI Development', 'Speech Recognition', 'Natural Language Processing'],
  },
  {
    category: 'Full Stack',
    items: ['React 19 + TypeScript', 'Node.js + Express', 'Ruby on Rails', 'PostgreSQL + MongoDB', 'REST + GraphQL'],
  },
  {
    category: 'Systems',
    items: ['CLI Tool Development', 'Database Architecture', 'Performance Optimization', 'Enterprise-Scale Apps'],
  },
]

export const experience: Experience[] = [
  {
    period: '2024 - Present',
    role: 'Founder & Lead Engineer',
    company: 'can.code Labs',
    highlights: [
      '80+ projects in two years: Next.js apps, Go CLIs, Ruby tools, React components',
      'Voice-AI ecosystem: speak-transcribe-respond workflows with agent memory',
      'Production Go CLI at 13K+ lines with SQLite, concurrent processing, API server',
      '90+ React components, 33+ custom hooks, real-time streaming architecture',
    ],
  },
  {
    period: '2022 - 2024',
    role: 'Software Engineer',
    company: 'Huntress',
    highlights: [
      'Microsoft 365 integration for enterprise security monitoring',
      'Processed security events across thousands of organizations',
      'Maintained 99.9%+ uptime for security-critical systems',
      'SRE rotation: production monitoring, incident response, root cause analysis',
    ],
  },
  {
    period: '2019 - 2022',
    role: 'Software Engineer',
    company: 'TaxJar (acquired by Stripe)',
    highlights: [
      'Automated state audit data requests: 2+ hours to 10-15 minutes',
      'Eliminated 100+ developer hours annually, zero false positives',
      'Sales tax engine at scale: 50K+ users, billions in annual transactions',
      'Rails, PostgreSQL, AWS S3 Glacier, database sharding, PII compliance',
    ],
    caseStudy: {
      title: 'Audit Data Automation',
      thesis: 'Faster wrong is still wrong.',
      beats: [
        {
          label: 'The real risk was correctness, not speed',
          detail:
            'TaxJar used database sharding. You could run a query, get data back that ' +
            'looked completely valid, but it came from the wrong shard. Wrong data sent to a ' +
            'state tax authority had legal consequences. Scripting the manual steps in sequence ' +
            'would have sped up the wrong outcome.',
        },
        {
          label: 'Worked backward from compliance artifacts, not the wiki',
          detail:
            'There was no spec. I sat with the compliance team and walked through actual ' +
            'past audit responses, then built validation chains: shard ID resolves from filing ' +
            'UUID, UUID belongs to the right customer, period falls within the amendment ' +
            'window. Each step gates on the previous one before any data gets pulled.',
        },
        {
          label: 'Validated against the full historical backlog, then ran in shadow mode',
          detail:
            'Compliance had a backlog of completed audit responses from manual runs. I diffed ' +
            'my output against every historical case and caught two edge cases nobody had ' +
            'documented: a timezone boundary that flipped shard resolution, and an inactive ' +
            'customer flag that changed the lookup path. Then shadow mode for weeks before ' +
            'cutover.',
        },
        {
          label: 'Automation improved security posture',
          detail:
            'Scoped Google Drive sharing with automatic expiration meant compliance reviewers ' +
            'got the files; developers never touched the PII. Met enterprise security ' +
            'requirements during the Stripe acquisition review.',
        },
      ],
      metrics: [
        '2+ hrs → 10-15 min',
        '100+ dev hours saved annually',
        'Zero wrong-shard incidents',
        '50K+ users, billions in transactions',
      ],
    },
  },
  {
    period: '2016 - 2019',
    role: 'Software Engineer',
    company: 'PaymentSpring / CardFlight',
    highlights: [
      'Core payment processing libraries for mobile SDK (iOS/Android)',
      'AWS SAM, Lambda, Step Functions, CloudFormation infrastructure-as-code',
      'Payment processor certifications: Vantiv/Worldpay, First Data',
      'Established engineering quality practices: RSpec, SimpleCov, RuboCop',
    ],
  },
  {
    period: '2007 - 2015',
    role: 'Lead Teacher',
    company: 'Prairie Hill Learning Center',
    highlights: [
      'Farm-based Montessori school for ages 18 months to 14 years',
      'Learned intrinsic motivation, meeting people where they are',
      'Origin of engineering approach: curiosity-driven problem solving',
    ],
  },
]

export const contact = {
  email: 'anderson.reinkordt+can@gmail.com',
  github: 'https://github.com/son1112',
  linkedin: 'https://linkedin.com/in/anderson-reinkordt',
}

export const specializations = [
  'AI Architecture & Integration',
  'Accessibility-First Development',
  'CLI & Developer Tools',
  'Voice-AI Applications',
  'Open-Source Development',
]
